import Modal from "../../Components/Modal"
import { twJoin } from "tailwind-merge"
import { channelAtom, planTokenAtom, roleAtom, userAtom, planAtom, fetchPlanAtom, isEditingStepAtom } from "../../state"
import { useAtom, useSetAtom } from "jotai"
import Button from "../../Components/Button"
import { useEffect, useState, useCallback, useRef } from "react"
import api from "../../services/api"
import { Formik, Form } from "formik"
import { cn } from "../../lib/utils"
import Spinner from "../../Components/Spinner"
import { PLAN_USER_ROLE } from "../../constants/planUserRole"
import { PLAN_STATUS } from "../../constants/planStatus"
import MentorActivityPanel from "../../Components/MentorActivityPanel"
import { sentryService } from "../../services/sentry"
import Information from "../../Components/Information"
import ErrorAlert from "../../Components/ErrorAlert"
const arrayKeys = [
  "identities",
  "gifts",
  "visions",
  "members",
  "encouragements",
  "commitments",
  "audio_blessings",
  "text_blessings",
]

// Map frontend step names to backend content keys for Pusher event matching
const stepNameToContentKey = {
  "Identity": "identities",
  "Gifts": "gifts",
  "Vision": "visions",
  "Mentors": ["members", "mentorCircle"], // members from PlanContent, mentorCircle from PlanUser operations
  "Encouragement": "encouragements",
  "Commitments": "commitments",
  "Blessing": ["audio_blessings", "text_blessings"], // Blessing can be either
}

// Helper to check if plan is past the invite-mentors step
const isPastInviteMentors = (status) => {
  const statusOrder = [
    PLAN_STATUS.CHOOSE_GUIDE,
    PLAN_STATUS.INVITE_STUDENT,
    PLAN_STATUS.SIT_WITH_STUDENT,
    PLAN_STATUS.INVITE_MENTORS,
    PLAN_STATUS.SEND_PLAN,
    PLAN_STATUS.CHECKLIST_COMPLETE,
    PLAN_STATUS.PLAN_COMPLETE,
  ]
  const currentIndex = statusOrder.indexOf(status)
  const inviteMentorsIndex = statusOrder.indexOf(PLAN_STATUS.INVITE_MENTORS)
  return currentIndex > inviteMentorsIndex
}

function Step(props) {
  const [role] = useAtom(roleAtom)
  const [channel, setChannel] = useAtom(channelAtom)
  const [plan] = useAtom(planAtom)
  const fetchPlan = useSetAtom(fetchPlanAtom)
  const setIsEditingStep = useSetAtom(isEditingStepAtom)

  const roleCanEdit = (role, type) => {
    switch (role) {
      case PLAN_USER_ROLE.GUIDE:
        return true
      case PLAN_USER_ROLE.INITIATOR:
      case PLAN_USER_ROLE.YOUTH:
        return type == "youth"
      case PLAN_USER_ROLE.MENTOR:
        return type == "mentors"
      default:
        return false
    }
  }

  const showMentorPanel = props.step.type === "mentors" && isPastInviteMentors(plan?.status)
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // Update global editing state to prevent parent from refreshing plan
  useEffect(() => {
    const isActivelyEditing = isOpen && isEditing
    setIsEditingStep(isActivelyEditing)

    // Cleanup: clear editing state when component unmounts
    return () => {
      setIsEditingStep(false)
    }
  }, [isOpen, isEditing, setIsEditingStep, props.step.name])
  const editable = roleCanEdit(role, props.step.type)
  const [planToken] = useAtom(planTokenAtom)
  const [user] = useAtom(userAtom)
  const [loading, setLoading] = useState(true)
  const [mentors, setMentors] = useState([])
  const [data, setData] = useState({
    ...props.keys.reduce((acc, key) => {
      if (arrayKeys.includes(key)) {
        acc[key] = []
        return acc
      } else {
        acc[key] = ""
        return acc
      }
    }, {}),
  })
  const [pendingData, setPendingData] = useState(null)
  const formikRef = useRef(null)
  const [hasPendingUpdates, setHasPendingUpdates] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // Memoized callback for Formik innerRef to prevent infinite loops
  const handleFormikRef = useCallback((formik) => {
    formikRef.current = formik
  }, [])

  // Fetch mentors for mentor-type steps
  useEffect(() => {
    if (planToken && props.step.type === "mentors") {
      api.getMentors(planToken).then(response => {
        setMentors(response.data.mentors || []);
      });
    }
  }, [planToken, props.step.type])

  const theme = props.theme || "brand"

  const isComplete = props.getIsComplete(data)
  const previewData = props.getPreviewData(data)

  useEffect(() => {
    if (!isOpen && isComplete) {
      setIsEditing(false)
    } else if (isOpen && !isComplete && editable) {
      setIsEditing(true)
    }
    // Apply pending updates when modal closes
    if (!isOpen && hasPendingUpdates) {
      refreshData(true) // Also refresh plan in case of mentor changes
    }
    if (!isOpen) {
      setSaveError(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (planToken) {
      refreshData()
    }
  }, [planToken])

  useEffect(() => {
    if (channel && planToken && user) {
      const handlePlanContentUpdate = (data) => {
        // Map step name to content key(s) for matching
        const expectedKeys = stepNameToContentKey[props.step.name]
        const matchesStep = Array.isArray(expectedKeys)
          ? expectedKeys.includes(data.step)
          : expectedKeys === data.step

        if (matchesStep && data.user_id != user.id) {
          // If user is actively editing, protect the form from updates
          // We check isEditing first - if they're in edit mode, assume they might have unsaved work
          if (isEditing && isOpen) {
            // Store pending updates instead of applying immediately
            setPendingData({ timestamp: Date.now() })
            setHasPendingUpdates(true)
          } else {
            // Safe to refresh immediately - user is viewing, not editing
            // For mentor circle changes, also refresh the entire plan to update roles/relationships
            const needsPlanRefresh = data.step === 'mentorCircle' || data.step === 'members'
            refreshData(needsPlanRefresh)
          }
        }
      }

      channel.bind("plan." + planToken, handlePlanContentUpdate)

      // Cleanup: unbind when component unmounts or dependencies change
      return () => {
        channel.unbind("plan." + planToken, handlePlanContentUpdate)
      }
    }
  }, [channel, planToken, user, props.step.name, fetchPlan, formikRef, isEditing, isOpen])

  const refreshData = async (alsoRefreshPlan = false) => {
    setLoading(true);

    try {
      // Special handling for mentors - fetch from Users/PlanUser instead of PlanContent
      if (props.keys.includes('mentors') && props.customFetch) {
        const response = await api.getMentors(planToken);
        setData({ mentors: response.data.mentors });
      } else {
        const response = await api.getPlanContent(planToken, props.keys);
        setData({
          ...props.keys.reduce((acc, key) => {
            if (
              key == "audio_blessings" ||
              key == "text_blessings" ||
              key == "encouragements" ||
              key == "commitments" ||
              key == "members"
            ) {
              acc[key] = response.data.filter(item => item.key == key).flatMap(item => item)
              return acc
            }
            if (arrayKeys.includes(key)) {
              acc[key] = response.data.filter(item => item.key == key).flatMap(item => item.value)
              return acc
            } else {
              acc[key] = response.data.find(item => item.key == key)?.value || ""
              return acc
            }
          }, {}),
        })
      }

      // If requested, also refresh the entire plan (for mentor changes)
      if (alsoRefreshPlan) {
        await fetchPlan(planToken)
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      sentryService.captureException(error);
    } finally {
      // Clear pending updates after successful refresh
      setPendingData(null)
      setHasPendingUpdates(false)
      setLoading(false);
    }
  }

  const formikOnSubmit = async (values, actions) => {
    await onSubmit(values)
  }

  const externalSubmit = async (values, actions) => {
    await props.onSubmit(values, actions, onSubmit)
  }

  const onSubmit = async (values, toggleEditing = true, reset = true) => {
    if (!values) {
      if (toggleEditing) {
        setIsEditing(false)
      }
      // Apply pending updates if user cancelled
      // Also refresh plan in case pending updates include mentor changes
      return await refreshData(hasPendingUpdates)
    }

    setSaveError(null)
    setLoading(true);
    try {
      await api.createPlanContent({
        reset,
        content: Object.keys(values)
          .map(key => {
            if (values[key] instanceof Array) {
              return values[key].map(item => {
                return {
                  plan_token: planToken,
                  key: key,
                  value: item,
                }
              })
            } else {
              return {
                plan_token: planToken,
                key: key,
                value: values[key],
              }
            }
          })
          .filter(v => v.value != "")
          .flat(),
      })
      if (toggleEditing) {
        setIsEditing(false)
      }
      await refreshData()
    } catch (error) {
      setSaveError("Something went wrong. Please try again.")
      setLoading(false);
    }
  }
  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-col rounded-md border hover:cursor-pointer hover:shadow",
        editable ? "bg-white" : "bg-neutral-100",
      )}
      onClick={() => {
        if (!loading) {
          setIsOpen(true)
        }
      }}
    >
      <div className={cn(
        "flex min-h-0 flex-1 flex-col p-4",
        isComplete && (theme === "identity" || theme === "brand") ? "bg-brand/20" : "",
        isComplete && theme === "gifts" ? "bg-gifts/20" : "",
        isComplete && theme === "vision" ? "bg-vision/20" : "",
        isComplete && theme === "circle" ? "bg-circle/20" : "",
      )}>
        <StepGrid
          props={props}
          editable={editable}
          isComplete={isComplete}
          previewData={previewData}
          loading={loading}
          mentors={mentors}
          data={data}
          user={user}
          theme={theme}
          setIsOpen={setIsOpen}
          setIsEditing={setIsEditing}
          showMentorPanel={showMentorPanel}
        />
        <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
          <StepOverview
            props={props}
            editable={editable}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={props.onSubmit ? externalSubmit : formikOnSubmit}
            previewData={previewData}
            theme={theme}
            data={data}
            isComplete={isComplete}
            image={props.image}
            refreshData={refreshData}
            mentors={mentors}
            user={user}
            showMentorPanel={showMentorPanel}
            hasPendingUpdates={hasPendingUpdates}
            handleFormikRef={handleFormikRef}
            saveError={saveError}
            onDismissSaveError={() => setSaveError(null)}
          />
        </Modal>
      </div>
    </div>
  )
}

function StepGrid({ props, editable, isComplete, previewData, loading, mentors, data, user, theme, setIsOpen, setIsEditing, showMentorPanel }) {
  // Get content items for the mentor panel based on step type
  const getContentItems = () => {
    if (theme === "encouragement") return data.encouragements || []
    if (theme === "commitments") return data.commitments || []
    if (theme === "blessings") return [...(data.audio_blessings || []), ...(data.text_blessings || [])]
    return []
  }

  const Content = () => {
    if (loading) {
      return <div className="w-full h-full flex flex-col justify-center align-middle items-center"><Spinner /></div>
    }
    // Show mentor panel for mentor-type steps when past invite-mentors (before checking isComplete)
    if (showMentorPanel && mentors.length > 0) {
      return (
        <div className="mt-4 hidden md:block">
          <MentorActivityPanel
            mentors={mentors}
            contentItems={getContentItems()}
            theme={theme}
            selectedMentorId={null}
            onMentorSelect={() => {}}
            onStartClick={() => {
              setIsOpen(true)
              setIsEditing(true)
            }}
            currentUserId={user?.id}
            editable={editable}
          />
        </div>
      )
    }
    if (isComplete) {
      return (
        <div className="mt-4 hidden md:block">
          <props.Preview data={previewData} editable={editable} theme={props.theme} onlyValue={true}/>
        </div>
      )
    }
    return (
      <StepEmpty name={props.step.name} type={props.step.type} editable={editable} view="grid" />
    )
  }

  return (
    <>
      <Heading
        title={props.step.name}
        type={props.step.type}
        editable={editable}
        isComplete={isComplete}
        loading={loading}
        image={props.image}
        description={props.description}
      />
      <Content />
    </>
  )
}

function StepOverview({
  props,
  editable,
  isEditing,
  onSubmit,
  isComplete,
  previewData,
  data,
  setIsEditing,
  image,
  theme,
  refreshData,
  mentors,
  user,
  showMentorPanel,
  hasPendingUpdates,
  handleFormikRef,
  saveError,
  onDismissSaveError,
}) {
  const getButtonBgColor = () => {
    switch (theme) {
      case "gifts":
        return "bg-gifts hover:bg-gifts/90"
      case "vision":
        return "bg-vision hover:bg-vision/90"
      case "circle":
        return "bg-circle hover:bg-circle/90"
      case "commitments":
        return "bg-commitments hover:bg-commitments/90"
      case "blessings":
        return "bg-blessings hover:bg-blessings/90"
      case "encouragement":
        return "bg-encouragement hover:bg-encouragement/90"
      default:
        return "bg-brand hover:bg-brand/90"
    }
  }

  // Get content items for the mentor panel based on step type
  const getContentItems = () => {
    if (theme === "encouragement") return data.encouragements || []
    if (theme === "commitments") return data.commitments || []
    if (theme === "blessings") return [...(data.audio_blessings || []), ...(data.text_blessings || [])]
    return []
  }

  return (
    <>
      <div className="mb-6 flex flex-col gap-4 border-b border-neutral-200 pb-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4 lg:justify-center">
          <img src={image} className="w-[72px]" />
          <h1 className="text-center text-3xl font-bold">{props.step.name}</h1>
          <div className="italic">{props.description}</div>
        </div>
      </div>

      {hasPendingUpdates && isEditing && editable && (
        <Information
          variant="info"
          title="Updates Available"
          description="Another user has made changes. Save or cancel your edits to see the latest updates."
          className="mb-4"
        />
      )}
      {saveError && (
        <ErrorAlert
          title="Save Failed"
          message={saveError}
          onDismiss={onDismissSaveError}
          className="mb-4"
        />
      )}

      {isEditing && editable ? (
        <Formik
          onSubmit={onSubmit}
          initialValues={{ ...data, ...props.initialValues }}
          validationSchema={props.schema}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize={false}
          innerRef={handleFormikRef}
        >
          {({ values }) => <Form>{props.children(values, data, refreshData)}</Form>}
        </Formik>
      ) : (
        <>
          <div className="mb-4 text-xl font-bold text-black">{props.step.title}</div>
          {isComplete ? (
            <>
              <props.Preview
                data={previewData}
                editable={editable}
                title={props.step.title}
                theme={theme}
                canInvite={true}
                onMigrate={async () => { await refreshData(); if (props.onMigrate) await props.onMigrate() }}
                onInvite={() => refreshData()}
              />
              {editable ? (
                <Button bgColor={getButtonBgColor(theme)} onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              ) : null}
            </>
          ) : showMentorPanel && mentors?.length > 0 ? (
            <MentorActivityPanel
              mentors={mentors}
              contentItems={getContentItems()}
              theme={theme}
              selectedMentorId={null}
              onMentorSelect={() => {}}
              onStartClick={() => setIsEditing(true)}
              currentUserId={user?.id}
              editable={editable}
            />
          ) : (
            <StepEmpty
              name={props.step.name}
              type={props.step.type}
              editable={false}
              view="overview"
            />
          )}
        </>
      )}
    </>
  )
}

function RoleDetails({ type }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border bg-neutral-50 px-8 py-4">
      <div>
        <span className="fa-stack">
          <i className="fa-solid fa-circle fa-stack-2x text-brand"></i>
          <i
            className={twJoin(
              "fa-solid fa-stack-1x text-white",
              type == "youth" ? "fa-user" : "fa-user-group",
            )}
          ></i>
        </span>
      </div>
      <div>
        <h2 className="font-bold text-brand">{type == "youth" ? "Student" : "Mentor"} Activity</h2>
        <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">
          {type == "mentors"
            ? "The mentors will complete this section"
            : "The student will complete this section"}
        </span>
      </div>
    </div>
  )
}

const Heading = ({ editable, isComplete, title, type, loading, image, description }) => {
  return (
    <div className="flex items-center justify-start gap-2">
      <div className="relative">
        {image ? (
          <>
            <img src={image} className="h-10 w-10" alt={title} />
            {isComplete && !loading ? (
              <i
                className={twJoin(
                  "fa-circle-check fa-solid absolute -right-1 -top-1 text-xl",
                  editable ? "text-stone-500" : "fa-solid text-neutral-400",
                )}
              ></i>
            ) : null}
          </>
        ) : (
          <span className="fa-stack">
            <i
              className={twJoin(
                "fa-solid fa-circle fa-stack-2x",
                editable ? "text-brand" : "text-neutral-300",
              )}
            ></i>
            <i
              className={twJoin(
                "fa-solid fa-stack-1x",
                type == "youth" ? "fa-user" : "fa-user-group",
                editable ? "text-white" : "text-white",
              )}
            ></i>
            {isComplete && !loading ? (
              <i
                className={twJoin(
                  "fa-circle-check fa-solid fa-stack -ml-[1px] -mt-7",
                  editable ? "text-stone-500" : "fa-solid text-neutral-400",
                )}
              ></i>
            ) : null}
          </span>
        )}
      </div>
      <h2 className={twJoin("text-xl font-bold text-black")}>{title}</h2>
      {description && <div className="text-sm italic">{description}</div>}
    </div>
  )
}

const StepEmpty = ({ editable, name, type, onClick, view }) => {
  return (
    <div
      className={twJoin(
        "w-full items-center justify-center flex-grow",
        view == "grid" ? "hidden md:flex" : "flex",
      )}
    >
      <div className="flex w-full flex-col items-center justify-center gap-2 py-8">
        <div className="text-lg font-bold italic text-black">
          {type == "mentors" ? "Mentor Activity" : "Student Activity"}
        </div>
        {editable ? (
          <Button variant="secondary" onClick={onClick} className={"py-1"}>
            Start
          </Button>
        ) : (
          <div className="flex w-full justify-center bg-neutral-100 py-8">
            <div className="flex max-w-[200px] flex-col items-center gap-2 text-center">
              <i className="fa-regular fa-rocket text-3xl text-neutral-400"></i>
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">
                {type == "mentors"
                  ? "Your mentors will complete section."
                  : "The student will complete section"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Step
